import React, { useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { PurchaseOrderRecord } from '@/types/domain/po-management';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Printer, Download, Building2, ShoppingBag, X } from 'lucide-react';

interface PoPrintPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  po: PurchaseOrderRecord | null;
}

export const PoPrintPreviewModal: React.FC<PoPrintPreviewModalProps> = ({
  isOpen,
  onClose,
  po,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, onClose]);

  if (!isOpen || !po) return null;

  const handlePrint = () => {
    window.print();
  };

  const watermarkText =
    po.status === 'DRAFT'
      ? 'DRAFT — NOT FOR COMMERCE'
      : po.status === 'CANCELLED'
      ? 'CANCELLED'
      : po.status === 'PENDING_RELEASE'
      ? 'PENDING AUTHORIZATION'
      : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs animate-in fade-in"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl p-8 bg-card border border-border shadow-2xl z-10 space-y-6"
      >
        <div className="flex items-center justify-between pb-4 border-b border-border/60">
          <div>
            <h3 className="text-xl font-extrabold text-foreground flex items-center gap-2">
              <ShoppingBag className="h-5 w-5 text-primary" /> Purchase Order Document Preview
            </h3>
            <p className="text-xs text-foreground-secondary">
              Official commercial purchase order representation for dispatch and legal archiving.
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg text-foreground-muted hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Printable Document Container */}
        <div id="printable-po-document" className="relative p-6 bg-white text-slate-900 rounded-xl border border-slate-200 shadow-xs space-y-6 my-2">
          {/* Watermark for non-issued orders */}
          {watermarkText && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10 select-none">
              <span className="text-6xl font-black tracking-widest text-slate-950 -rotate-30 uppercase">
                {watermarkText}
              </span>
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 pb-6 border-b border-slate-200">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-primary font-black text-xl tracking-tight">
                <Building2 className="h-6 w-6" /> ProcureAI Global Enterprise Inc.
              </div>
              <p className="text-xs text-slate-500">100 Innovation Parkway, Suite 500, Austin, TX 78701</p>
              <p className="text-xs text-slate-500">Tax ID / EIN: 74-9823101 | DUNS: 08-234-9912</p>
            </div>

            <div className="text-right space-y-1">
              <span className="text-2xl font-black text-slate-900 tracking-tight block">PURCHASE ORDER</span>
              <div className="text-sm font-bold text-slate-700">PO Number: <span className="text-primary font-black">{po.poNumber}</span></div>
              {po.sapPoNumber && (
                <div className="text-xs font-semibold text-purple-700">SAP Doc #: {po.sapPoNumber}</div>
              )}
              <div className="text-xs text-slate-500">Order Date: {formatDate(po.poDate)}</div>
              <div className="text-xs text-slate-500">Revision: Rev {po.version}</div>
            </div>
          </div>

          {/* Vendor & Shipping Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
              <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">VENDOR / SUPPLIER</span>
              <p className="font-extrabold text-sm text-slate-900">{po.vendorName}</p>
              <p className="text-slate-600">Vendor Code: {po.vendorCode}</p>
              <p className="text-slate-600">Payment Terms: {po.paymentTerms}</p>
              <p className="text-slate-600">Incoterms: {po.incoterms}</p>
            </div>

            <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
              <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">SHIP TO / DELIVERY DESTINATION</span>
              <p className="font-extrabold text-sm text-slate-900">{po.plant}</p>
              <p className="text-slate-600">{po.shippingAddress}</p>
              <p className="text-slate-600">Promised Delivery: {formatDate(po.promisedDeliveryDate)}</p>
              <p className="text-slate-600">Shipping Method: {po.deliveryTerms}</p>
            </div>
          </div>

          {/* Line Items Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-300 text-slate-600 font-extrabold uppercase text-[10px]">
                  <th className="py-2.5 px-2">#</th>
                  <th className="py-2.5 px-2">Material / Description</th>
                  <th className="py-2.5 px-2 text-right">Qty</th>
                  <th className="py-2.5 px-2">UOM</th>
                  <th className="py-2.5 px-2 text-right">Unit Price</th>
                  <th className="py-2.5 px-2 text-right">Tax (%)</th>
                  <th className="py-2.5 px-2 text-right">Total ({po.currency})</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {po.items.map((item, idx) => (
                  <tr key={item.id || idx}>
                    <td className="py-2 px-2 font-mono text-slate-400">{item.lineNumber}</td>
                    <td className="py-2 px-2">
                      <span className="font-bold text-slate-900 block">{item.description}</span>
                      <span className="text-[10px] text-slate-500 font-mono">Code: {item.materialCode}</span>
                    </td>
                    <td className="py-2 px-2 text-right font-bold text-slate-900">{item.quantityOrdered}</td>
                    <td className="py-2 px-2 text-slate-600">{item.uom}</td>
                    <td className="py-2 px-2 text-right font-mono">{formatCurrency(item.unitPrice, po.currency)}</td>
                    <td className="py-2 px-2 text-right font-mono">{item.taxPercent}%</td>
                    <td className="py-2 px-2 text-right font-extrabold text-slate-900 font-mono">
                      {formatCurrency(item.lineTotal, po.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Commercial Breakdown */}
          <div className="flex justify-end pt-4 border-t border-slate-200">
            <div className="w-64 space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal:</span>
                <span className="font-mono font-medium">{formatCurrency(po.subtotal, po.currency)}</span>
              </div>
              {po.discount > 0 && (
                <div className="flex justify-between text-emerald-600 font-medium">
                  <span>Discount:</span>
                  <span className="font-mono">- {formatCurrency(po.discount, po.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-slate-600">
                <span>Tax / VAT (18%):</span>
                <span className="font-mono font-medium">{formatCurrency(po.tax, po.currency)}</span>
              </div>
              {po.freight > 0 && (
                <div className="flex justify-between text-slate-600">
                  <span>Freight & Handling:</span>
                  <span className="font-mono font-medium">{formatCurrency(po.freight, po.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-black text-slate-900 pt-2 border-t border-slate-300">
                <span>Total Commitment:</span>
                <span className="font-mono text-primary">{formatCurrency(po.totalValue, po.currency)}</span>
              </div>
            </div>
          </div>

          {/* Standard Terms & Signatures */}
          <div className="pt-6 border-t border-slate-200 text-[10px] text-slate-500 space-y-2">
            <p className="font-bold text-slate-700">TERMS AND CONDITIONS:</p>
            <p>
              1. Goods must be delivered strictly according to the stated delivery schedule. Partial deliveries must be pre-authorized.
              2. Invoice must reference PO #{po.poNumber} and be accompanied by signed proof of delivery (GRN).
              3. Payment will be processed strictly in accordance with agreed terms ({po.paymentTerms}) post 3-way reconciliation.
            </p>
            <div className="grid grid-cols-2 gap-8 pt-6 mt-4 border-t border-slate-200">
              <div>
                <div className="h-10 border-b border-slate-300 flex items-end font-script text-slate-700 italic">
                  {po.buyer}
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase">Authorized Procurement Buyer Signature</span>
              </div>
              <div>
                <div className="h-10 border-b border-slate-300 flex items-end text-slate-400">
                  Pending Vendor Counter-Sign
                </div>
                <span className="text-[10px] font-bold text-slate-500 uppercase">Supplier Acceptance Signature</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/40">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-xl font-bold border-border">
            Close Preview
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} className="rounded-xl font-bold border-border">
            <Printer className="h-4 w-4 mr-1.5" /> Print PO
          </Button>
          <Button size="sm" onClick={handlePrint} className="rounded-xl font-bold bg-primary text-white">
            <Download className="h-4 w-4 mr-1.5" /> Export PDF
          </Button>
        </div>
      </div>
    </div>
  );
};
